import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal } from '@kss/ui';
import { Clock } from 'lucide-react';
import { logout as logoutAction, refreshSession } from './authSlice';
import type { AuthState } from './types';
import { useIdleTimer } from './useIdleTimer';
import { IDLE_COUNTDOWN_MS } from './sessionTimeout.config';

type RootWithAuth = { auth: AuthState };

/**
 * Inactivity-warning modal.
 *
 * Flow:
 *   1. `useIdleTimer` reports `isIdle === true` after
 *      `IDLE_WARN_AFTER_MS` of no user activity.
 *   2. Modal opens with a `IDLE_COUNTDOWN_MS` (60 s) live countdown.
 *   3. User clicks "Stay signed in" → POST /auth/refresh, dispatch
 *      `refreshSession` with the new tokens, reset idle timer, close.
 *   4. User clicks "Sign out now" → dispatch `logout('manual')`.
 *   5. Countdown reaches 0 with no action → dispatch
 *      `logout('inactive')` (banner explains the reason).
 *
 * Mounted once at the root of both WebLayout and PanelLayout. Renders
 * nothing until the user is idle, so there's zero cost during normal
 * use. The modal does NOT steal focus on open (Enter on the page is
 * still safe) but `autoFocus` lands on the primary action so a
 * single Enter keypress extends the session.
 *
 * Bug ref: AI Agent 2026-06-11 — "session time out and inactivity
 * popup, click OK continue, else logout".
 */
export function SessionTimeoutModal() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s: RootWithAuth) => s.auth.isAuthenticated);
  const session = useSelector((s: RootWithAuth) => s.auth.session);
  const { isIdle, reset } = useIdleTimer(isAuthenticated);

  /* Countdown starts when the modal opens. We anchor it to a
     monotonic deadline (`deadlineRef`) instead of decrementing a
     counter so tab-throttled timers don't drift the countdown. */
  const deadlineRef = useRef<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(IDLE_COUNTDOWN_MS);
  const [busy, setBusy] = useState<'refresh' | 'logout' | null>(null);

  useEffect(() => {
    if (!isIdle || !isAuthenticated) {
      deadlineRef.current = null;
      setRemainingMs(IDLE_COUNTDOWN_MS);
      return;
    }
    /* Modal just opened — set the deadline. */
    if (deadlineRef.current === null) {
      deadlineRef.current = Date.now() + IDLE_COUNTDOWN_MS;
    }
    const tick = window.setInterval(() => {
      if (deadlineRef.current === null) return;
      const left = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(left);
      if (left <= 0) {
        window.clearInterval(tick);
        /* Auto-logout on countdown elapse. Use the 'inactive' reason
           so the banner explains exactly why they were signed out. */
        dispatch(logoutAction('inactive'));
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [isIdle, isAuthenticated, dispatch]);

  /* "Stay signed in" — refresh the access token, update store, close. */
  const handleStay = async () => {
    if (busy) return;
    if (!session?.refreshToken || !session?.sessionId) {
      /* No refresh token to use — fall through to logout. The
         `refresh-failed` reason makes the banner say so. */
      dispatch(logoutAction('refresh-failed'));
      return;
    }
    setBusy('refresh');
    try {
      /* Dynamic import avoids a circular dep between `@kss/auth` and
         `@kss/api` at module-load time (api depends on auth's types
         via the client config layer). */
      const api = await import('@kss/api');
      const next = await api.authService.refresh({
        refreshToken: session.refreshToken,
        sessionId: session.sessionId,
      });
      dispatch(refreshSession({
        accessToken: next.accessToken,
        refreshToken: next.refreshToken,
        expiresAt: next.expiresAt,
        tenantId: next.tenantId,
        sessionId: next.sessionId,
      }));
      reset();
      deadlineRef.current = null;
      setRemainingMs(IDLE_COUNTDOWN_MS);
    } catch {
      /* Refresh failed (token revoked, network down, etc.) — sign
         the user out with a banner that explains it wasn't them. */
      dispatch(logoutAction('refresh-failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleLogout = () => {
    if (busy) return;
    setBusy('logout');
    dispatch(logoutAction('manual'));
  };

  if (!isAuthenticated || !isIdle) return null;

  const secondsLeft = Math.ceil(remainingMs / 1000);

  return (
    <Modal
      open
      /* Backdrop click is suppressed — the user must make an explicit
         choice. Passing a no-op `onClose` keeps the Modal API happy
         without giving them a silent escape hatch. */
      onClose={() => undefined}
      title="Session about to expire"
      width={460}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline" onClick={handleLogout} disabled={busy !== null}>
            Sign out now
          </Button>
          <Button onClick={handleStay} disabled={busy !== null} autoFocus>
            {busy === 'refresh' ? 'Extending…' : 'Stay signed in'}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--color-warning-light)] text-[var(--color-warning)]">
          <Clock className="h-5 w-5" />
        </span>
        <div className="text-sm">
          <p className="text-content-primary">
            You've been inactive for a while. For your security we'll sign
            you out unless you confirm you're still here.
          </p>
          <p className="mt-3 text-content-secondary">
            Auto-logout in{' '}
            <strong className={`tabular-nums ${secondsLeft <= 10 ? 'text-danger' : 'text-content-primary'}`}>
              {secondsLeft}
            </strong>{' '}
            second{secondsLeft === 1 ? '' : 's'}…
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default SessionTimeoutModal;
